// Toast — a transient notice in the corner of the screen.
//
// For telling someone what just happened when there is no question to ask.
// `window.alert` does the same job by seizing the page: it blocks until
// dismissed, it cannot be styled, and it is the wrong shape for "that
// worked" — the reader has to close a modal to confirm they read a line
// they had already read.
//
// Prop-driven, like ConfirmDialog next door: no context, no provider to
// mount at the app root. A screen keeps its own list with useToasts() and
// renders one <ToastStack>. That is the whole API.
//
//   const { toasts, push, dismiss } = useToasts();
//   push('success', 'Schedule #3 ran');
//   push('danger', `Run failed: ${message}`);
//   …
//   <ToastStack toasts={toasts} onDismiss={dismiss} />
//
// A success disappears on its own; a FAILURE does not. An error message is
// the one thing on screen the reader may need to copy, re-read or act on,
// and a message that removes itself mid-sentence is a message that has to
// be reproduced to be read again.

import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import '@platform/ui/Toast/Toast.css';

const AUTO_DISMISS_MS = { success: 4000, info: 4000, warning: 8000, danger: 0 };

const ICONS = {
  success: <><path d="M20 6L9 17l-5-5" /></>,
  info: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>,
  warning: <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  danger: <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>,
};

let seq = 0;

export function useToasts() {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    const timer = timers.current.get(id);
    if (timer) { clearTimeout(timer); timers.current.delete(id); }
    setToasts(list => list.filter(t => t.id !== id));
  }, []);

  const push = useCallback((tone, message, opts = {}) => {
    seq += 1;
    const id = `toast-${seq}`;
    setToasts(list => [...list, { id, tone, message, title: opts.title || null }]);
    const ms = opts.duration != null ? opts.duration : (AUTO_DISMISS_MS[tone] ?? 4000);
    if (ms > 0) {
      timers.current.set(id, setTimeout(() => dismiss(id), ms));
    }
    return id;
  }, [dismiss]);

  // A screen can be navigated away from while a toast is still counting.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  return { toasts, push, dismiss };
}

export function ToastStack({ toasts, onDismiss }) {
  if (typeof document === 'undefined' || !toasts?.length) return null;

  return createPortal(
    <div className="ztoast-region" aria-live="polite" aria-atomic="false">
      {toasts.map(t => (
        <div key={t.id} className={`ztoast ztoast-${t.tone}`} role={t.tone === 'danger' ? 'alert' : 'status'}>
          <svg className="ztoast-icon" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {ICONS[t.tone] || ICONS.info}
          </svg>
          <div className="ztoast-text">
            {t.title && <div className="ztoast-title">{t.title}</div>}
            <div className="ztoast-message">{t.message}</div>
          </div>
          <button type="button" className="ztoast-close" onClick={() => onDismiss(t.id)} aria-label="Dismiss">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}

export default ToastStack;
